import { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface AgbModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAccept: () => void
}

export function AgbModal({ open, onOpenChange, onAccept }: AgbModalProps) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    // Use a small threshold (5px) to account for rounding errors
    if (scrollHeight - scrollTop <= clientHeight + 5) {
      setHasScrolledToBottom(true)
    }
  }

  // Reset scroll state when modal opens
  useEffect(() => {
    if (open) {
      setHasScrolledToBottom(false)
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Allgemeine Geschäftsbedingungen (AGB)</DialogTitle>
        </DialogHeader>
        
        <div 
          ref={scrollRef}
          onScroll={handleScroll}
          className="prose prose-invert my-4 min-h-0 max-w-none flex-1 overflow-y-auto pr-4 text-xs md:text-sm"
        >
          <div className="space-y-6">
            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">1. Geltungsbereich</h2>
              <p className="text-muted-foreground">
                Diese Allgemeinen Geschäftsbedingungen gelten für sämtliche Buchungen und Vertragsabschlüsse über Kurse, 10er-Blöcke und Abonnements von motionLAB, Inhaberin Mag. Christina Hohenwarter, Südring 211, 9020 Klagenfurt am Wörthersee.
                Mit der Buchung eines Angebots akzeptiert der Kunde diese AGB.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">2. Leistungsangebot</h2>
              <p className="text-muted-foreground">
                motionLAB bietet betreute Gruppentrainingseinheiten in unterschiedlichen Formaten (z. B. Functional Training, Mobility, Yoga, Conditioning etc.) an.
                Mit dem Erwerb eines 10er-Blocks oder Abonnements besteht Zugang zum gesamten jeweils angebotenen Kursprogramm. Es erfolgt keine Bindung an eine bestimmte Kursart.
                Ein Anspruch auf Durchführung eines bestimmten Trainers oder einer bestimmten Kursart besteht nicht.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">3. Vertragsabschluss</h2>
              <p className="text-muted-foreground">
                Die Buchung erfolgt online über das Buchungssystem oder vor Ort. Der Vertrag kommt mit Bestätigung der Buchung zustande.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">4. Preise und Zahlungsbedingungen</h2>
              <p className="text-muted-foreground">
                Es gelten die zum Buchungszeitpunkt veröffentlichten Preise. Zahlungen erfolgen vor Kursbeginn über das angebotene Zahlungsmittel. Bei Zahlungsverzug behält sich motionLAB vor, die Teilnahme zu verweigern.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">5. Einzelstunden</h2>
              <p className="text-muted-foreground">
                Einzelstunden berechtigen zur einmaligen Teilnahme an einem gebuchten Kurs.
                Eine Stornierung ist bis 12 Stunden vor Kursbeginn kostenfrei möglich.
                Bei späterer Stornierung oder Nichterscheinen wird die Einheit vollständig verrechnet.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">6. 10er-Block</h2>
              <p className="text-muted-foreground">
                Ein 10er-Block berechtigt zur Teilnahme an 10 frei wählbaren Kursen aus dem gesamten Kursangebot.
                Eine Rückerstattung nicht genutzter Einheiten ist ausgeschlossen.
                Eine Weitergabe an Dritte ist nicht zulässig.
                Stornofrist pro gebuchtem Kurs: 12 Stunden vor Beginn. Bei späterer Absage verfällt die Einheit.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">7. Abonnements (Basic / Premium)</h2>
              <div className="text-muted-foreground space-y-4">
                <div>
                  <h3 className="font-semibold text-foreground">Basic-Abo</h3>
                  <p>
                    Das Basic-Abo berechtigt zur Teilnahme an maximal einem Kurs pro Woche. Der jeweils verfügbare Credit ist bis Sonntag, 23:59 Uhr, gültig. Ab Montag, 00:00 Uhr, wird der neue, für die jeweilige Kalenderwoche gültige Credit bereitgestellt.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Premium-Abo</h3>
                  <p>
                    Das Premium-Abo berechtigt zur Teilnahme an maximal zwei Kursen pro Woche. Die jeweils verfügbaren Credits sind bis Sonntag, 23:59 Uhr, gültig. Ab Montag, 00:00 Uhr, werden die neuen, für die jeweilige Kalenderwoche gültigen Credits bereitgestellt.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Laufzeit, Kündigung und Pausierung</h3>
                  <p>
                    Das Abonnement wird auf unbestimmte Zeit abgeschlossen und kann unter Einhaltung einer Kündigungsfrist von einem Monat zum Monatsende schriftlich per E-Mail an <a href="mailto:office@motionlab-klagenfurt.at" className="text-primary hover:underline">office@motionlab-klagenfurt.at</a> gekündigt werden. Im Krankheitsfall kann das Abonnement ab einer Dauer von drei Wochen gegen Vorlage einer ärztlichen Bestätigung pausiert werden.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">8. Widerrufsrecht (Fernabsatz)</h2>
              <p className="text-muted-foreground">
                Bei Online-Buchung besteht grundsätzlich ein 14-tägiges Widerrufsrecht gemäß FAGG.
                Der Kunde erklärt sich ausdrücklich damit einverstanden, dass die Leistung vor Ablauf der Widerrufsfrist beginnt.
                Wird die Leistung innerhalb der Widerrufsfrist teilweise in Anspruch genommen, ist im Falle eines Widerrufs ein anteiliger Betrag zu bezahlen.
                Bei vollständiger Vertragserfüllung innerhalb der Widerrufsfrist erlischt das Widerrufsrecht.
                Ein Widerruf ist schriftlich per E-Mail an <a href="mailto:office@motionlab-klagenfurt.at" className="text-primary hover:underline">office@motionlab-klagenfurt.at</a> zu richten.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">9. Gesundheit und Eigenverantwortung</h2>
              <p className="text-muted-foreground">
                Die Teilnahme erfolgt auf eigene Verantwortung.
                Der Kunde bestätigt, dass keine gesundheitlichen Bedenken gegen die Teilnahme bestehen.
                Bei bestehenden Beschwerden oder Schwangerschaft ist vorab ärztlicher Rat einzuholen.
                Trainer:innen ersetzen keine medizinische Behandlung.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">10. Haftung</h2>
              <p className="text-muted-foreground">
                motionLAB haftet nur für Schäden, die auf grobe Fahrlässigkeit oder Vorsatz zurückzuführen sind.
                Für persönliche Gegenstände wird keine Haftung übernommen.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">11. Änderungen des Kursplans</h2>
              <p className="text-muted-foreground">
                motionLAB behält sich vor:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4">
                <li>Kurse zeitlich zu verschieben</li>
                <li>Trainer zu wechseln</li>
                <li>Kurse bei zu geringer Teilnehmerzahl abzusagen</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                Bei Absage durch motionLAB erfolgt keine Verrechnung.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">12. Hausordnung</h2>
              <p className="text-muted-foreground">
                Den Anweisungen der Trainer:innen ist Folge zu leisten.
                Unsachgemäße Nutzung von Equipment kann zum Ausschluss vom Training führen.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">13. Datenschutz</h2>
              <p className="text-muted-foreground">
                Es gilt die auf der Website veröffentlichte Datenschutzerklärung.
              </p>
            </section>

            <section>
              <h2 className="font-headline text-base md:text-lg font-semibold mb-2 text-primary">14. Gerichtsstand und anwendbares Recht</h2>
              <p className="text-muted-foreground">
                Es gilt österreichisches Recht.
                Gerichtsstand ist Klagenfurt am Wörthersee.
              </p>
            </section>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {!hasScrolledToBottom && (
            <p className="text-xs text-muted-foreground flex items-center justify-center">
              Bitte scrolle bis zum Ende, um zu akzeptieren.
            </p>
          )}
          <Button 
            onClick={onAccept} 
            disabled={!hasScrolledToBottom}
            className="w-full sm:w-auto"
          >
            Ich akzeptiere die AGB
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
